import { TicketType } from 'store/models/TicketTypeHelper';
import { StatusCountI } from 'store/models/NumTickets';
import { EachWorkdeskTicketI } from 'store/models/workdesk-tickets/each-workdesk-ticket.interface';
import MatchHelper from 'store/models/MatchHelper';
import { WorkdeskTickets } from 'store/models/workdesk-tickets';
import {
  EachSortBy,
  LoadWorkdeskTicketsParams,
  AllWorkdeskTicketsI,
  TicketsMap,
} from 'store/models/workdesk-tickets/workdesk-tickets.interface';

/**
 * [status]: {
 *  [ticketType]: {
 *      inboxes: string[],
 *      sortByHasLoaded: {
 *          sortBy: {field:string, dir:string},
 *          hasLoaded: { .... }
 *      }[]
 *  }[]
 * }
 */

export interface EachInboxesSortByHasLoaded {
  inboxes: string[];
  sortByHasLoaded: EachSortByHasLoaded[];
}

export interface EachSortByHasLoaded {
  sortBy: EachSortBy[];
  hasLoaded: EachHasLoaded;
}
export interface StoreHasLoadedI {
  [status: string]: {
    [ticketType: string]: EachInboxesSortByHasLoaded[];
  };
}

export interface EachHasLoaded {
  inboxes: string[];
  status: string;
  ticketType: TicketType;
  sortBy: EachSortBy[];
  // TODO: might not separated by page anymore if switch to do scroll instead of pagination
  ticketIds: string[];
  hasGotAllResults?: boolean;
}

class HasLoadedHelper {
  hasLoaded: StoreHasLoadedI;
  ticketsMap: TicketsMap;
  constructor(hasLoaded: StoreHasLoadedI, ticketsMap: TicketsMap) {
    this.hasLoaded = hasLoaded;
    this.ticketsMap = ticketsMap; // will be used to look up information
  }
  static getRemovedIdsHasLoaded(
    hasLoaded: StoreHasLoadedI,
    ticketIds: string[],
  ): StoreHasLoadedI {
    const removeTheIdHasLoaded = Object.entries<{
      [ticketType: string]: EachInboxesSortByHasLoaded[];
    }>(hasLoaded).reduce(
      (accHasLoaded, [hStatus, hItem]) => ({
        ...accHasLoaded,
        [hStatus]: Object.entries<EachInboxesSortByHasLoaded[]>(
          hItem,
        ).reduce(
          (accTicketType, [hTicketType, hInboxesSbHl]) => ({
            ...accTicketType,
            [hTicketType]: hInboxesSbHl.map((inboxesSbHl) => ({
              ...inboxesSbHl,
              sortByHasLoaded: inboxesSbHl.sortByHasLoaded.map(
                (sbHl: EachSortByHasLoaded) => {
                  return {
                    ...sbHl,
                    hasLoaded: {
                      ...sbHl.hasLoaded,
                      ticketIds: sbHl.hasLoaded.ticketIds.filter(
                        // if find won't take. only take ticketId that is not in the ticketIds
                        (ticketId) =>
                          ticketIds.findIndex(
                            (id) => id === ticketId,
                          ) < 0,
                      ),
                    },
                  };
                },
              ),
            })),
          }),
          {},
        ),
      }),
      {},
    );
    return removeTheIdHasLoaded;
  }

  static getDefaultForSubHasLoadedFromLoadParams(
    loadParams: LoadWorkdeskTicketsParams,
  ): EachHasLoaded {
    return {
      inboxes: loadParams.inboxes,
      ticketIds: [], // will be replaced later
      status: loadParams.status,
      ticketType: loadParams.ticketType,
      sortBy: loadParams.sortBy,
      hasGotAllResults: false, // will be replaced later
    };
  }

  static getDefaultForSortByHasLoadedFromLoadParams(
    loadParams: LoadWorkdeskTicketsParams,
  ): EachSortByHasLoaded[] {
    return [
      {
        sortBy: loadParams.sortBy,
        hasLoaded: HasLoadedHelper.getDefaultForSubHasLoadedFromLoadParams(
          loadParams,
        ),
      },
    ];
  }

  static getDefaultForTicketTypeFromLoadParams(
    loadParams: LoadWorkdeskTicketsParams,
  ): EachInboxesSortByHasLoaded[] {
    return [
      {
        inboxes: loadParams.inboxes,
        sortByHasLoaded: HasLoadedHelper.getDefaultForSortByHasLoadedFromLoadParams(
          loadParams,
        ),
      },
    ];
  }

  static getDefaultForStatusFromLoadParams(
    loadParams: LoadWorkdeskTicketsParams,
  ): { [ticketType: string]: EachInboxesSortByHasLoaded[] } {
    return {
      [loadParams.ticketType]: HasLoadedHelper.getDefaultForTicketTypeFromLoadParams(
        loadParams,
      ),
    };
  }

  hasStatus(status: string): boolean {
    return this.getStatus(status) ? true : false;
  }

  getStatus(
    status: string,
  ): { [ticketType: string]: EachInboxesSortByHasLoaded[] } {
    return this.hasLoaded[status];
  }

  hasStatusWithTicketType(
    status: string,
    ticketType: TicketType,
  ): boolean {
    return this.getStatusWithTicketType(status, ticketType)
      ? true
      : false;
  }

  getStatusWithTicketType(
    status: string,
    ticketType: TicketType,
  ): undefined | EachInboxesSortByHasLoaded[] {
    const thatStatus = this.getStatus(status);
    return thatStatus && thatStatus[ticketType];
  }

  static inboxesSortByHasLoadedHasExactInboxes(
    inboxesSortByHasLoaded: EachInboxesSortByHasLoaded[],
    inboxes: string[],
  ): boolean {
    return inboxesSortByHasLoaded.some((item) =>
      MatchHelper.matchedExactInboxes(item.inboxes, inboxes),
    );
  }

  static eachInboxesSortByHasLoadedHasExactSortBy(
    eachInboxesSortByHasLoaded: EachInboxesSortByHasLoaded,
    sortBy: EachSortBy[],
  ) {
    return eachInboxesSortByHasLoaded.sortByHasLoaded.some((item) =>
      MatchHelper.matchedExactSortByArray(item.sortBy, sortBy),
    );
  }

  hasStatusWithTicketTypeAndExactInboxes(
    status: string,
    ticketType: TicketType,
    inboxes: string[],
  ): boolean {
    const thatTicketType = this.getStatusWithTicketType(
      status,
      ticketType,
    );
    if (!thatTicketType) return false;
    return HasLoadedHelper.inboxesSortByHasLoadedHasExactInboxes(
      thatTicketType,
      inboxes,
    );
  }

  static getMatchedTickets(
    tickets: EachWorkdeskTicketI[],
    almostLoadParams: {
      // from the loop
      inboxes: string[];
      status: string;
      ticketType: TicketType;
    },
  ): EachWorkdeskTicketI[] {
    const { inboxes, status, ticketType } = almostLoadParams;
    const filtered = tickets.filter(
      (ticket: EachWorkdeskTicketI) =>
        MatchHelper.ticketMatchedSi(ticket, ticketType) &&
        MatchHelper.ticketMatchedInboxes(ticket, inboxes) &&
        MatchHelper.ticketMatchedStatus(ticket, status),
    );
    return filtered;
  }

  hasAllIdsInTicketsMap(ticketIds: string[]): boolean {
    return ticketIds.every((ticketId) => ticketId in this.ticketsMap);
  }

  ticketIdsOutsideTicketsMap(ticketIds: string[]): string[] {
    return ticketIds.filter(
      (ticketId) => !(ticketId in this.ticketsMap),
    );
  }

  sortTicketIdsWithLoadParams(
    ticketIds: string[],
    loadParamsThisRound: LoadWorkdeskTicketsParams,
  ) {
    if (!ticketIds.every((ticketId) => ticketId in this.ticketsMap)) {
      console.error(
        `this.ticketsMap`,
        this.ticketsMap,
        `ticketIds`,
        ticketIds,
      );
      throw Error(`all ticketIds need to be in ticketsMap`);
    }
    const tickets = ticketIds.map(
      (ticketId) => this.ticketsMap[ticketId],
    );
    return (WorkdeskTickets.getSortedTicketsBySortBy(
      tickets,
      loadParamsThisRound.sortBy,
    ) as EachWorkdeskTicketI[]).map((ticket) => ticket.id);
  }

  static getUniqueStatusesFromTickets(
    tickets: EachWorkdeskTicketI[],
  ) {
    const statuses = tickets.map((ticket) => ticket.status);
    return Array.from(new Set(statuses));
  }

  /**
   * mutate this.hasLoaded so not returning hasLoaded
   * note might change if react complains about mutation
   *
   * don't filter by params. the updated could be outside of the current filter
   * @param ticketType
   * @param tickets
   * @param loadParamsInput
   */
  private _moveTicketsForEachTicketType(
    ticketType: TicketType,
    tickets: EachWorkdeskTicketI[],
    loadParamsInput: LoadWorkdeskTicketsParams,
    fromWs: boolean,
    statusCount?: StatusCountI,
  ): void {
    const loadParamsG = { ...loadParamsInput, ticketType };

    const statuses = HasLoadedHelper.getUniqueStatusesFromTickets(
      tickets,
    );

    statuses.forEach((status) => {
      const loadParams = { ...loadParamsG, status };
      //   check if already have status
      if (!this.hasStatus(status)) {
        //   add default (not add ids yet)
        this.hasLoaded = {
          ...this.hasLoaded,
          [status]: HasLoadedHelper.getDefaultForStatusFromLoadParams(
            loadParams,
          ),
        };
      }

      //   check if already have ticketType
      if (!this.hasStatusWithTicketType(status, ticketType)) {
        //   add default (not add ids yet)
        this.hasLoaded = {
          ...this.hasLoaded,
          [status]: {
            ...(this.hasLoaded[status] || {}),
            [ticketType]: HasLoadedHelper.getDefaultForTicketTypeFromLoadParams(
              loadParams,
            ),
          },
        };
      }

      //   check if already have params inboxes
      const hasStatusWithExactIb = this.hasStatusWithTicketTypeAndExactInboxes(
        status,
        ticketType,
        loadParams.inboxes,
      );
      if (!hasStatusWithExactIb) {
        //   add default (not add ids yet)
        this.hasLoaded = {
          ...this.hasLoaded,
          [status]: {
            ...(this.hasLoaded[status] || {}),
            [ticketType]: this.hasLoaded[status][ticketType].concat(
              HasLoadedHelper.getDefaultForTicketTypeFromLoadParams(
                loadParams,
              ),
            ),
          },
        };
      }

      //   check if already have sortBy
      this.hasLoaded[status][ticketType].forEach(
        (eachInboxesSortByHasLoaded: EachInboxesSortByHasLoaded) => {
          // check if already have the sortby -> if not -> add
          if (
            MatchHelper.matchedExactInboxes(
              eachInboxesSortByHasLoaded.inboxes,
              loadParams.inboxes,
            )
          ) {
            const hasSortBy = HasLoadedHelper.eachInboxesSortByHasLoadedHasExactSortBy(
              eachInboxesSortByHasLoaded,
              loadParams.sortBy,
            );
            if (!hasSortBy) {
              // if not already have the sortby  -> add
              eachInboxesSortByHasLoaded.sortByHasLoaded = eachInboxesSortByHasLoaded.sortByHasLoaded.concat(
                HasLoadedHelper.getDefaultForSortByHasLoadedFromLoadParams(
                  loadParams,
                ),
              );
            }
          }
        },
      );

      const totalTicketsLength = tickets.length;

      // TODO: go through the hasLoaded -> add proper ids
      const forTicketType = this.hasLoaded[status][ticketType].map(
        (eachInboxesSortByHasLoaded: EachInboxesSortByHasLoaded) => {
          if (
            MatchHelper.matchedAnyInboxes(
              eachInboxesSortByHasLoaded.inboxes,
              loadParams.inboxes,
            )
          ) {
            // only go in if this hasLoaded inboxes overlaps with the loadParams
            const matchedTickets = HasLoadedHelper.getMatchedTickets(
              tickets,
              {
                status,
                ticketType,
                inboxes: eachInboxesSortByHasLoaded.inboxes,
              },
            );
            console.debug(
              `tickets: ${totalTicketsLength}. matched: ${matchedTickets.length}`,
            );
            const matchedTicketsIds = matchedTickets.map(
              (ticket) => ticket.id,
            );
            const matchedLength = matchedTickets.length;

            const forSortByHasLoaded = eachInboxesSortByHasLoaded.sortByHasLoaded.map(
              (eachSortByHasLoaded: EachSortByHasLoaded) => {
                const loadParamsThisRound = {
                  ...loadParams, // loadParams for this ticketType loop
                  inboxes: eachInboxesSortByHasLoaded.inboxes,
                  sortBy: eachSortByHasLoaded.sortBy,
                };
                if (
                  !fromWs && // won't check and slice if fromWs
                  MatchHelper.matchLoadParams(
                    loadParamsThisRound,
                    loadParamsInput, // original loadParams
                  )
                ) {
                  // if matched the exact loadParams => slice at the startAt and throw out the old data
                  //   if match exact the tickets and the matched tickets supposed to be the same length
                  if (
                    !loadParamsInput.search &&
                    totalTicketsLength !== matchedLength
                  ) {
                    const errorToThrow = `at this loadParams ${JSON.stringify(
                      loadParamsThisRound,
                    )}. matched (${matchedLength}) should be the same length as tickets passed (${totalTicketsLength})`;
                    console.error(
                      errorToThrow,
                      `tickets passed: `,
                      tickets,
                      'matched: ',
                      matchedTickets,
                    );
                    throw Error(errorToThrow);
                    //   if gets in here then something might be wrong with the matched function
                  } else if (!loadParamsInput.search) {
                    // if has search won't slice
                    // slice at the startAt and throw out the old data
                    const prevLength =
                      eachSortByHasLoaded.hasLoaded.ticketIds.length;
                    // TODO: when @natchapol fix the sort. remove the sort here in the frontend
                    const toAppend = eachSortByHasLoaded.hasLoaded.ticketIds.slice(
                      loadParams.startAt + matchedTicketsIds.length,
                    );
                    const sortMatched = this.sortTicketIdsWithLoadParams(
                      matchedTicketsIds,
                      loadParamsThisRound,
                    );
                    eachSortByHasLoaded = {
                      ...eachSortByHasLoaded,
                      hasLoaded: {
                        ...eachSortByHasLoaded.hasLoaded,
                        ticketIds: WorkdeskTickets.deduplicateIds(
                          eachSortByHasLoaded.hasLoaded.ticketIds
                            .slice(0, loadParams.startAt)
                            .concat(sortMatched)
                            .concat(toAppend),
                        ),
                      },
                    };
                    // TODO: remove fromWs and fromWsAt?
                    console.debug(
                      `tickets: *** slice *** at `,
                      loadParams.startAt,
                      {
                        matched: matchedTicketsIds.length,
                        tickets: tickets.length,
                        'all prev': prevLength,
                        'all after':
                          eachSortByHasLoaded.hasLoaded.ticketIds
                            .length,
                        statusCount,
                        toAppend: toAppend.length,
                        sortMatched: sortMatched,
                      },
                    );
                    const newLength =
                      eachSortByHasLoaded.hasLoaded.ticketIds.length;
                    if (
                      statusCount &&
                      statusCount[loadParamsInput.status] &&
                      newLength ===
                        statusCount[loadParamsInput.status]
                    ) {
                      eachSortByHasLoaded = {
                        ...eachSortByHasLoaded,
                        hasLoaded: {
                          ...eachSortByHasLoaded.hasLoaded,
                          hasGotAllResults: true,
                        },
                      };
                    }
                  } else {
                    // when search?
                    console.debug(
                      `tickets: *** slice *** at NO SLICE because on Search. startAt:`,
                      loadParams.startAt,
                    );
                    /**
                     * cannot slice because hasLoaded keeps everything not just the shown on search
                     * so if sliced, won't get the correct data
                     */
                    eachSortByHasLoaded = {
                      ...eachSortByHasLoaded,
                      hasLoaded: {
                        ...eachSortByHasLoaded.hasLoaded,
                        ticketIds: this.sortTicketIdsWithLoadParams(
                          WorkdeskTickets.deduplicateIds(
                            eachSortByHasLoaded.hasLoaded.ticketIds.concat(
                              matchedTicketsIds,
                            ),
                          ),
                          loadParamsThisRound,
                        ),
                        hasGotAllResults: false,
                      },
                    };
                  }
                } else {
                  //   if not matched exact with the original loadParams just concat the ids
                  eachSortByHasLoaded = {
                    ...eachSortByHasLoaded,
                    hasLoaded: {
                      ...eachSortByHasLoaded.hasLoaded,
                      ticketIds: this.sortTicketIdsWithLoadParams(
                        WorkdeskTickets.deduplicateIds(
                          eachSortByHasLoaded.hasLoaded.ticketIds.concat(
                            matchedTicketsIds,
                          ),
                        ),
                        loadParamsThisRound,
                      ),
                    },
                  };
                }
                return eachSortByHasLoaded;
              },
            );
            return {
              ...eachInboxesSortByHasLoaded,
              sortByHasLoaded: forSortByHasLoaded,
            };
          }
          return eachInboxesSortByHasLoaded;
        },
      );

      this.hasLoaded = {
        ...this.hasLoaded,
        [status]: {
          ...(this.hasLoaded[status] || {}),
          [ticketType]: forTicketType,
        },
      };
    });
  }

  /**
   * move tickets from this.hasLoaded
   * remove tickets and add to the right place
   *
   *
   * note after assign, status could change to be outside of the current
   * so don't filter with loadParams
   * @param ticketsInput
   * @param workdeskFilter
   * @param statusCount
   * @param fromWs
   */
  moveTickets(
    ticketsInputBefore: AllWorkdeskTicketsI,
    loadParams: LoadWorkdeskTicketsParams,
    fromWs = false, // use for check has got all result ?
    statusCount?: StatusCountI, // use for check has got all result ? // TODO: use this or remove it
    fromScroll = false,
  ): StoreHasLoadedI {
    console.time('** TIME ** moveTickets');
    // if filter, after assign and change status will bug out
    // const ticketsInput = ticketsInputBefore.filter((ticket) =>
    //   MatchHelper.ticketMatchedStatus(ticket, loadParams.status),
    // );
    const ticketsInput = ticketsInputBefore;
    const ticketIds = ticketsInput.map((ticket) => ticket.id);
    const tickets = ticketsInput.map((ticket) => ({
      ...((this.ticketsMap && this.ticketsMap[ticket.id]) || {}),
      ...ticket,
    }));
    // make sure ticketMap has all ticketIds in -> if not then should update the ticketMap
    const ticketIdsOutside = this.ticketIdsOutsideTicketsMap(
      ticketIds,
    );
    // eidther add to ticketMap or throw Error
    if (ticketIdsOutside.length) {
      throw Error(
        `has ticketIds outside of ticketsMap (${ticketIdsOutside.length}) ${ticketIdsOutside}`,
      );
    }

    console.debug(
      `********* in moveTickets: fromScroll ${fromScroll} ******`,
    );

    /**
     * if from non-search and on scroll, will not remove id, will slice
     * if from search, will remove id
     * if from ws, will remove id as well (in case there is a move of status or non-si <-> si)
     */
    if (!fromScroll && !loadParams.search) {
      this.hasLoaded = HasLoadedHelper.getRemovedIdsHasLoaded(
        this.hasLoaded,
        ticketIds,
      );
    }

    const ticketTypesFromTicket = tickets.length
      ? WorkdeskTickets.getUniqueTicketTypesOfTickets(tickets)
      : [loadParams.ticketType]; // for when tickets length is 0 -> will affect mark hasGotAllResults

    /**
     * only loop through ticketTypes (at most 2 rounds)
     */
    ticketTypesFromTicket.forEach((ticketType) => {
      // mutate so no return
      this._moveTicketsForEachTicketType(
        ticketType,
        tickets,
        loadParams,
        fromWs,
        statusCount,
      );
    });

    // TODO: sort the ids -> create a new function to sort
    console.timeEnd('** TIME ** moveTickets');
    // TODO: handle when no tickets from rest api
    return this.hasLoaded;
  }

  /**
   * not mutate this.hasLoaded
   * @param params
   */
  removeTicketsAtParams(
    params: LoadWorkdeskTicketsParams,
  ): StoreHasLoadedI {
    if (
      !this.hasStatusWithTicketTypeAndExactInboxes(
        params.status,
        params.ticketType,
        params.inboxes,
      )
    ) {
      return this.hasLoaded;
    } else {
      const updatedAtTicketType = this.hasLoaded[params.status][
        params.ticketType
      ].map((eachInboxesSortByHasLoaded) => {
        if (
          MatchHelper.matchedExactInboxes(
            eachInboxesSortByHasLoaded.inboxes,
            params.inboxes,
          )
        ) {
          const hasSortBy = HasLoadedHelper.eachInboxesSortByHasLoadedHasExactSortBy(
            eachInboxesSortByHasLoaded,
            params.sortBy,
          );
          if (hasSortBy) {
            // if not already have the sortby  -> add
            const updatedSortByHasLoaded = eachInboxesSortByHasLoaded.sortByHasLoaded.map(
              (eachSortByHasLoaded) => {
                if (
                  MatchHelper.matchedExactSortByArray(
                    eachSortByHasLoaded.sortBy,
                    params.sortBy,
                  )
                ) {
                  return {
                    ...eachSortByHasLoaded,
                    hasLoaded: {
                      ...eachSortByHasLoaded.hasLoaded,
                      ticketIds: [],
                    },
                  };
                } else {
                  return eachSortByHasLoaded;
                }
              },
            );
            return {
              ...eachInboxesSortByHasLoaded,
              sortByHasLoaded: updatedSortByHasLoaded,
            };
          }
        }
        return eachInboxesSortByHasLoaded;
      });
      return {
        ...this.hasLoaded,
        [params.status]: {
          ...this.hasLoaded[params.status],
          [params.ticketType]: updatedAtTicketType,
        },
      };
    }
  }
}

export default HasLoadedHelper;
